import { BooMark } from "@/components/boo/BooMark";
import { personName } from "@/components/boo/access-copy";
import { InlineError } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { api, type ContainerRow } from "@/lib/api";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { useState } from "react";

const ANY = "any";

// For someone who can open a Boo but not manage it (often arriving from the
// Boo's own "request access" link in chat): asks its owners and admins, who
// get an email that opens the request ready to send.
export function AskAdminsDialog({
  boo,
  candidates,
  initialSourceId,
  onClose,
}: {
  boo: { id: string; name: string };
  candidates: ContainerRow[];
  initialSourceId?: string | null;
  onClose: () => void;
}) {
  const members = useQuery({ queryKey: ["boo-members", boo.id], queryFn: () => api.containers.members.list(boo.id) });
  const admins = (members.data?.data ?? []).filter((m) => m.role === "owner" || m.role === "admin");
  const others = candidates.filter((b) => b.id !== boo.id && b.agentType === "boo-brain" && b.status !== "deleted");
  const [sourceId, setSourceId] = useState<string>(initialSourceId && others.some((b) => b.id === initialSourceId) ? initialSourceId : ANY);
  const [note, setNote] = useState("");
  const ask = useMutation({
    mutationFn: () => api.containers.connections.askAdmins(boo.id, { sourceId: sourceId === ANY ? null : sourceId, note: note.trim() || null }),
    onSuccess: (res) => {
      if (res.sent) toast.success(`Asked ${res.to.slice(0, 2).join(" and ")}${res.to.length > 2 ? ` and ${res.to.length - 2} more` : ""}.`);
      else if (res.reason === "already_asked") toast.info("You already asked today.");
      else toast.info(`${boo.name} has no owner yet. Ask an owner or admin of your organization.`);
      onClose();
    },
  });
  const noAdmins = members.isSuccess && admins.length === 0;

  return (
    <Dialog open onOpenChange={(open) => !open && !ask.isPending && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Ask an admin</DialogTitle>
          <DialogDescription>Only the admins of {boo.name} can ask other Boos for access.</DialogDescription>
        </DialogHeader>

        {members.isSuccess && (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
            {noAdmins ? (
              <span className="text-muted-foreground">It has no owner yet. Ask an owner or admin of your organization.</span>
            ) : (
              <>
                <span className="text-muted-foreground">Goes to</span>
                <span className="font-medium [overflow-wrap:anywhere]">
                  {admins.slice(0, 3).map((a) => personName(a)).join(", ")}
                  {admins.length > 3 ? ` +${admins.length - 3}` : ""}
                </span>
              </>
            )}
          </div>
        )}

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="ask-source">Learn from</Label>
          <Select value={sourceId} onValueChange={setSourceId}>
            <SelectTrigger id="ask-source" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Any Boo, let them choose</SelectItem>
              {others.map((b) => (
                <SelectItem key={b.id} value={b.id}>
                  <span className="flex items-center gap-2">
                    <BooMark id={b.id} className="size-4" />
                    {b.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="ask-note">Note (optional)</Label>
          <Textarea id="ask-note" rows={2} maxLength={500} value={note} placeholder="What it should know" onChange={(e) => setNote(e.target.value)} />
        </div>
        {ask.isError && <InlineError error={ask.error} />}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={ask.isPending}>
            Cancel
          </Button>
          <Button onClick={() => ask.mutate()} loading={ask.isPending} disabled={noAdmins || members.isLoading}>
            Ask them
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
